import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './CandidateSourcing.css';

const CandidateSourcing = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [isLoading, setIsLoading] = useState(true);
  const [isFeaturesOpen, setIsFeaturesOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 1500);

    const handleScroll = () => {
      const isScrolled = window.scrollY > 50;
      setScrolled(isScrolled);
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      clearTimeout(timer);
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const handleDropdownClick = (path) => {
    navigate(path);
    setIsFeaturesOpen(false);
    setIsMobileMenuOpen(false);
  };

  const handleAccessRecruit = () => {
    setIsFormOpen(true);
  };

  const handleSignIn = () => {
    navigate('/signin');
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();
    alert('Thank you for your interest! We will contact you soon.');
    setIsFormOpen(false);
  };

  const sourcingStats = [
    { number: '85%', text: 'Faster Hiring Process' },
    { number: '3.2x', text: 'More Qualified Candidates' },
    { number: '40%', text: 'Cost Reduction in Hiring' },
    { number: '92%', text: 'Candidate Satisfaction Rate' },
    { number: '10K+', text: 'Active Talent Pool' },
    { number: '78%', text: 'Passive Candidates Reached' }
  ];

  const sourcingMethods = [
    {
      title: 'AI-Powered Candidate Matching',
      description: 'Advanced algorithms analyze skills, experience, and cultural fit to identify perfect matches for your roles.',
      icon: '🤖',
      features: ['Skill-based matching', 'Cultural fit analysis', 'Predictive success scoring']
    },
    {
      title: 'Multi-Channel Social Sourcing',
      description: 'Leverage LinkedIn, GitHub, Stack Overflow, and professional networks to find passive candidates.',
      icon: '🔍',
      features: ['LinkedIn integration', 'GitHub profiling', 'Professional network scraping']
    },
    {
      title: 'Intelligent Talent Pool Management',
      description: 'Build, segment, and maintain relationships with qualified candidates for future opportunities.',
      icon: '👥',
      features: ['Candidate segmentation', 'Automated nurturing', 'Skill tagging']
    },
    {
      title: 'Personalized Automated Outreach',
      description: 'Scale your candidate engagement with personalized messaging and follow-up sequences.',
      icon: '📧',
      features: ['Email templates', 'Sequence automation', 'Response tracking']
    },
    {
      title: 'University & Campus Recruitment',
      description: 'Connect with fresh talent through campus drives, career fairs, and university partnerships.',
      icon: '🎓',
      features: ['Campus partnerships', 'Internship programs', 'Graduate hiring']
    },
    {
      title: 'Employee Referral Program',
      description: 'Leverage your existing team network to source high-quality candidates through referrals.',
      icon: '⭐',
      features: ['Referral tracking', 'Incentive management', 'Social sharing']
    }
  ];

  const benefits = [
    'Reduce time-to-hire by up to 70%',
    'Access to 85% passive candidate market',
    'Improve candidate quality and retention',
    'Lower cost per hire by 40-60%',
    'Enhanced employer branding and presence',
    'Data-driven hiring decisions with analytics',
    'Build sustainable talent pipelines',
    'Competitive advantage in talent market',
    'Better diversity and inclusion metrics',
    'Scalable recruitment processes'
  ];

  const sourcingChannels = [
    { name: 'LinkedIn Sourcing', efficiency: '95%', candidates: '50M+', icon: '💼' },
    { name: 'GitHub Technical', efficiency: '88%', candidates: '10M+', icon: '⚡' },
    { name: 'Stack Overflow', efficiency: '82%', candidates: '8M+', icon: '🔧' },
    { name: 'Professional Networks', efficiency: '75%', candidates: '15M+', icon: '🌐' },
    { name: 'University Databases', efficiency: '90%', candidates: '5M+', icon: '🎓' },
    { name: 'Referral Networks', efficiency: '96%', candidates: '2M+', icon: '🤝' }
  ];

  if (isLoading) {
    return (
      <div className="talent-sourcing-loading">
        <div className="talent-sourcing-spinner"></div>
        <h2>Loading Advanced Candidate Sourcing...</h2>
      </div>
    );
  }

  const RecruitFormPopup = () => (
    <div className={`talent-form-popup ${isFormOpen ? 'talent-active' : ''}`}>
      <div className="talent-form-overlay" onClick={() => setIsFormOpen(false)}></div>
      <div className="talent-form-container">
        <button className="talent-form-close" onClick={() => setIsFormOpen(false)}>×</button>
        <h2>Access HRCoPilot Sourcing</h2>
        <p>Transform your talent acquisition with our advanced sourcing platform</p>
        <form onSubmit={handleFormSubmit} className="talent-recruit-form">
          <div className="talent-form-group">
            <label>Company Name *</label>
            <input type="text" required placeholder="Enter your company name" />
          </div>
          <div className="talent-form-group">
            <label>Email Address *</label>
            <input type="email" required placeholder="Enter your work email" />
          </div>
          <div className="talent-form-group">
            <label>Monthly Hiring Needs *</label>
            <select required>
              <option value="">Select hiring volume</option>
              <option value="1-5">1-5 hires per month</option>
              <option value="6-15">6-15 hires per month</option>
              <option value="16-30">16-30 hires per month</option>
              <option value="30+">30+ hires per month</option>
            </select>
          </div>
          <div className="talent-form-group">
            <label>Primary Sourcing Challenges</label>
            <select multiple>
              <option value="passive">Finding passive candidates</option>
              <option value="quality">Candidate quality</option>
              <option value="time">Time-consuming process</option>
              <option value="diversity">Diversity sourcing</option>
              <option value="cost">High sourcing costs</option>
            </select>
          </div>
          <button type="submit" className="talent-btn-primary talent-btn-full">
            Access Sourcing Platform
          </button>
        </form>
      </div>
    </div>
  );

  return (
    <div className="talent-sourcing-container">
      <RecruitFormPopup />
      
      {/* Enhanced Header */}
      <header className={`talent-platform-header ${scrolled ? 'talent-scrolled' : ''}`}>
        <div className="talent-container">
          <div className="talent-header-content">
            <div className="talent-logo">
              <span className="talent-logo-icon">🚀</span>
              <span className="talent-logo-text">HRCoPilot Recruit</span>
            </div>

            <button
              className="talent-mobile-menu-toggle"
              onClick={toggleMobileMenu}
            >
              <span></span>
              <span></span>
              <span></span>
            </button>

            <nav className={`talent-nav-menu ${isMobileMenuOpen ? 'talent-mobile-open' : ''}`}>
              <ul className="talent-nav-list">
                <li
                  className={`talent-nav-item ${isFeaturesOpen ? 'talent-active' : ''}`}
                  onMouseEnter={() => setIsFeaturesOpen(true)}
                  onMouseLeave={() => setIsFeaturesOpen(false)}
                >
                  <span className="talent-nav-link">Hiring Solutions</span>
                  {isFeaturesOpen && (
                    <div className="talent-dropdown-menu">
                      <div className="talent-dropdown-content">
                        <div className="talent-dropdown-section">
                          <h4>Source & Attract</h4>
                          <ul>
                            <li onClick={() => handleDropdownClick('/candidate-sourcing')}>Candidate sourcing</li>
                            <li onClick={() => handleDropdownClick('/job-advertising')}>Job advertising</li>
                            <li onClick={() => handleDropdownClick('/careers-site')}>Careers site</li>
                            <li onClick={() => handleDropdownClick('/social-recruiting')}>Social Recruiting</li>
                            <li onClick={() => handleDropdownClick('/employee-referral')}>Employee referral</li>
                          </ul>
                        </div>
                        <div className="talent-dropdown-section">
                          <h4>Track & Engage</h4>
                          <ul>
                            <li onClick={() => handleDropdownClick('/hiring-pipeline')}>Hiring pipeline</li>
                            <li onClick={() => handleDropdownClick('/resume-management')}>Resume management</li>
                            <li onClick={() => handleDropdownClick('/assessments')}>Assessments</li>
                            <li onClick={() => handleDropdownClick('/video-interview')}>Video Interview</li>
                            <li onClick={() => handleDropdownClick('/hiring-analytics')}>Hiring analytics</li>
                          </ul>
                        </div>
                        <div className="talent-dropdown-section">
                          <h4>Automate & Hire</h4>
                          <ul>
                            <li onClick={() => handleDropdownClick('/ai-recruitment')}>AI Recruitment</li>
                            <li onClick={() => handleDropdownClick('/offer-letter')}>Offer letter</li>
                            <li onClick={() => handleDropdownClick('/onboarding')}>Onboarding</li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  )}
                </li>
                <li className="talent-nav-item">
                  <span className="talent-nav-link">Pricing Plans</span>
                </li>
                <li className="talent-nav-item">
                  <span className="talent-nav-link">HR Resources</span>
                </li>
                <li className="talent-nav-item">
                  <span className="talent-nav-link">Client Support</span>
                </li>
              </ul>
            </nav>

            <div className="talent-header-actions">
              <button className="talent-btn-secondary" onClick={handleSignIn}>HR Portal</button>
              <button className="talent-btn-primary" onClick={handleAccessRecruit}>Access Recruit</button>
            </div>
          </div>
        </div>
      </header>

      {/* Enhanced Hero Section */}
      <section className="talent-sourcing-hero">
        <div className="talent-container">
          <div className="talent-sourcing-hero-content">
            <div className="talent-hero-text">
              <div className="talent-hero-badge">
                <span>AI-Powered Talent Discovery Platform</span>
              </div>
              <h1 className="talent-sourcing-title">
                Find & Engage Top Talent 
                <span className="talent-gradient-text"> 3x Faster</span>
              </h1>
              <p className="talent-sourcing-subtitle">
                Discover, connect, and build relationships with qualified candidates using our intelligent 
                sourcing platform. Access millions of passive candidates and transform your talent acquisition.
              </p>
              <div className="talent-hero-actions">
                <button className="talent-btn-primary talent-btn-large" onClick={handleAccessRecruit}>
                 START SOURCING NOW
                </button>
                <button className="talent-btn-outline talent-btn-large">
                  <span className="talent-play-icon">▶</span>
                  Watch Sourcing Demo
                </button>
              </div>
              <div className="talent-hero-stats">
                <div className="talent-stat-item">
                  <div className="talent-stat-number">10M+</div>
                  <div className="talent-stat-label">Candidate Profiles</div>
                </div>
                <div className="talent-stat-item">
                  <div className="talent-stat-number">85%</div>
                  <div className="talent-stat-label">Passive Candidates</div>
                </div>
                <div className="talent-stat-item">
                  <div className="talent-stat-number">3.2x</div>
                  <div className="talent-stat-label">More Responses</div>
                </div>
              </div>
            </div>
            <div className="talent-sourcing-hero-visual">
              <div className="talent-sourcing-dashboard">
                <div className="talent-dashboard-header">
                  <div className="talent-dashboard-dots">
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>
                  <div className="talent-dashboard-title">Talent Sourcing Dashboard</div>
                </div>
                <div className="talent-dashboard-content">
                  <div className="talent-candidate-grid">
                    {[1, 2, 3, 4, 5, 6].map((item) => (
                      <div key={item} className="talent-candidate-card">
                        <div className="talent-candidate-avatar"></div>
                        <div className="talent-candidate-info">
                          <div className="talent-candidate-name"></div>
                          <div className="talent-candidate-title"></div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Enhanced What is Candidate Sourcing */}
      <section className="talent-sourcing-definition">
        <div className="talent-container">
          <div className="talent-sourcing-definition-grid">
            <div className="talent-definition-content">
              <h2>What is Advanced Candidate Sourcing?</h2>
              <p>
                Candidate sourcing is the strategic process of proactively identifying, attracting, and engaging 
                qualified individuals for current or future positions. Unlike traditional recruiting that waits 
                for applicants, sourcing takes a proactive approach to build talent pipelines.
              </p>
              <p>
                In today's competitive talent market, <strong>85% of the workforce consists of passive candidates</strong> 
                who aren't actively job hunting. Effective sourcing helps you reach this untapped talent pool 
                and build relationships before your competitors even know they exist.
              </p>
              <div className="talent-key-points">
                <div className="talent-key-point">
                  <span className="talent-point-icon">🎯</span>
                  <div>
                    <h4>Proactive Approach</h4>
                    <p>Find candidates before you have open roles</p>
                  </div>
                </div>
                <div className="talent-key-point">
                  <span className="talent-point-icon">📈</span>
                  <div>
                    <h4>Pipeline Building</h4>
                    <p>Create sustainable talent streams</p>
                  </div>
                </div>
                <div className="talent-key-point">
                  <span className="talent-point-icon">💡</span>
                  <div>
                    <h4>Strategic Advantage</h4>
                    <p>Stay ahead in competitive markets</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="talent-definition-visual">
              <div className="talent-sourcing-process">
                <div className="talent-sourcing-process-step">
                  <div className="talent-sourcing-step-icon">🔍</div>
                  <h4>Identify</h4>
                  <p>Find potential candidates across multiple channels</p>
                </div>
                <div className="talent-process-connector"></div>
                <div className="talent-sourcing-process-step">
                  <div className="talent-sourcing-step-icon">👋</div>
                  <h4>Engage</h4>
                  <p>Personalized outreach and connection</p>
                </div>
                <div className="talent-process-connector"></div>
                <div className="talent-sourcing-process-step">
                  <div className="talent-sourcing-step-icon">💼</div>
                  <h4>Qualify</h4>
                  <p>Assess skills, experience, and cultural fit</p>
                </div>
                <div className="talent-process-connector"></div>
                <div className="talent-sourcing-process-step">
                  <div className="talent-sourcing-step-icon">📥</div>
                  <h4>Nurture</h4>
                  <p>Build relationships for future opportunities</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Enhanced Stats Section */}
      <section className="talent-sourcing-stats">
        <div className="talent-container">
          <h2 className="talent-sourcing-section-title">Proven Sourcing Results</h2>
          <div className="talent-sourcing-stats-grid">
            {sourcingStats.map((stat, index) => (
              <div key={index} className="talent-sourcing-stat-card">
                <h3>{stat.number}</h3>
                <p>{stat.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Enhanced Sourcing Channels */}
      <section className="talent-sourcing-channels">
        <div className="talent-container">
          <h2 className="talent-sourcing-section-title">Multi-Channel Sourcing Approach</h2>
          <p className="talent-section-subtitle">Access candidates across all major professional platforms</p>
          <div className="talent-channels-grid">
            {sourcingChannels.map((channel, index) => (
              <div key={index} className="talent-channel-card">
                <div className="talent-channel-icon">{channel.icon}</div>
                <h4>{channel.name}</h4>
                <div className="talent-channel-metrics">
                  <div className="talent-channel-metric">
                    <span className="talent-metric-value">{channel.efficiency}</span>
                    <span className="talent-metric-label">Efficiency</span>
                  </div>
                  <div className="talent-channel-metric">
                    <span className="talent-metric-value">{channel.candidates}</span>
                    <span className="talent-metric-label">Candidates</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Enhanced Tabs Section */}
      <section className="talent-sourcing-tabs-section">
        <div className="talent-container">
          <div className="talent-sourcing-tabs-header">
            <button 
              className={`talent-sourcing-tab ${activeTab === 'overview' ? 'talent-sourcing-tab-active' : ''}`}
              onClick={() => setActiveTab('overview')}
            >
              Sourcing Strategy
            </button>
            <button 
              className={`talent-sourcing-tab ${activeTab === 'methods' ? 'talent-sourcing-tab-active' : ''}`}
              onClick={() => setActiveTab('methods')}
            >
              Sourcing Methods
            </button>
            <button 
              className={`talent-sourcing-tab ${activeTab === 'benefits' ? 'talent-sourcing-tab-active' : ''}`}
              onClick={() => setActiveTab('benefits')}
            >
              Key Benefits
            </button>
            <button 
              className={`talent-sourcing-tab ${activeTab === 'tools' ? 'talent-sourcing-tab-active' : ''}`}
              onClick={() => setActiveTab('tools')}
            >
              Sourcing Tools
            </button>
          </div>

          <div className="talent-sourcing-tab-content">
            {activeTab === 'overview' && (
              <div className="talent-sourcing-tab-panel">
                <h3>Strategic Talent Sourcing Framework</h3>
                <p>
                  Effective candidate sourcing requires a systematic approach that combines technology, 
                  data, and human expertise. Our framework ensures you're not just finding candidates, 
                  but building sustainable talent pipelines.
                </p>
                <div className="talent-strategy-grid">
                  <div className="talent-strategy-item">
                    <h4>📊 Data-Driven Approach</h4>
                    <p>Leverage analytics to identify sourcing patterns, success metrics, and optimize your outreach strategies based on real data.</p>
                  </div>
                  <div className="talent-strategy-item">
                    <h4>🤖 AI-Powered Discovery</h4>
                    <p>Use machine learning algorithms to identify candidates who match your specific requirements across multiple platforms.</p>
                  </div>
                  <div className="talent-strategy-item">
                    <h4>🔗 Multi-Channel Integration</h4>
                    <p>Seamlessly source from LinkedIn, GitHub, professional networks, and internal databases in one unified platform.</p>
                  </div>
                  <div className="talent-strategy-item">
                    <h4>📈 Pipeline Management</h4>
                    <p>Build and maintain talent pools segmented by skills, experience levels, and interest for future hiring needs.</p>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'methods' && (
              <div className="talent-sourcing-tab-panel">
                <div className="talent-sourcing-methods-grid">
                  {sourcingMethods.map((method, index) => (
                    <div key={index} className="talent-sourcing-method-card">
                      <div className="talent-sourcing-method-icon">{method.icon}</div>
                      <h4>{method.title}</h4>
                      <p>{method.description}</p>
                      <ul className="talent-method-features">
                        {method.features.map((feature, idx) => (
                          <li key={idx}>{feature}</li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {activeTab === 'benefits' && (
              <div className="talent-sourcing-tab-panel">
                <h3>Comprehensive Benefits of Advanced Sourcing</h3>
                <div className="talent-benefits-container">
                  <div className="talent-benefits-list">
                    {benefits.map((benefit, index) => (
                      <div key={index} className="talent-sourcing-benefit-item">
                        <span className="talent-sourcing-benefit-check">✓</span>
                        <span>{benefit}</span>
                      </div>
                    ))}
                  </div>
                  <div className="talent-benefits-visual">
                    <div className="talent-benefits-stats">
                      <div className="talent-benefit-stat">
                        <div className="talent-stat-circle talent-stat-85">
                          <span>85%</span>
                        </div>
                        <p>Faster Hiring</p>
                      </div>
                      <div className="talent-benefit-stat">
                        <div className="talent-stat-circle talent-stat-78">
                          <span>78%</span>
                        </div>
                        <p>Cost Reduction</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'tools' && (
              <div className="talent-sourcing-tab-panel">
                <h3>Advanced Sourcing Technology Stack</h3>
                <div className="talent-tools-grid">
                  <div className="talent-tool-card">
                    <h4>AI Candidate Matching</h4>
                    <p>Intelligent algorithms that analyze 50+ data points to find perfect matches</p>
                    <div className="talent-tool-features">
                      <span>Skill Analysis</span>
                      <span>Cultural Fit</span>
                      <span>Success Prediction</span>
                    </div>
                  </div>
                  <div className="talent-tool-card">
                    <h4>Automated Outreach</h4>
                    <p>Personalized messaging at scale with A/B testing and performance analytics</p>
                    <div className="talent-tool-features">
                      <span>Email Sequences</span>
                      <span>Response Tracking</span>
                      <span>Template Library</span>
                    </div>
                  </div>
                  <div className="talent-tool-card">
                    <h4>Talent CRM</h4>
                    <p>Complete candidate relationship management with engagement tracking</p>
                    <div className="talent-tool-features">
                      <span>Pipeline Management</span>
                      <span>Activity Tracking</span>
                      <span>Nurture Campaigns</span>
                    </div>
                  </div>
                  <div className="talent-tool-card">
                    <h4>Analytics Dashboard</h4>
                    <p>Real-time insights into sourcing performance and candidate engagement</p>
                    <div className="talent-tool-features">
                      <span>Performance Metrics</span>
                      <span>Source Analytics</span>
                      <span>ROI Tracking</span>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Enhanced Final CTA Section */}
      <section className="talent-sourcing-final-cta">
        <div className="talent-container">
          <div className="talent-cta-content">
            <h2>Ready to Revolutionize Your Talent Sourcing?</h2>
            <p>Join 10,000+ companies using HRCoPilot to build world-class talent pipelines</p>
            <div className="talent-cta-buttons">
              <button className="talent-btn-primary talent-btn-large" onClick={handleAccessRecruit}>
                Start Sourcing Free Trial
              </button>
              <button className="talent-btn-outline talent-btn-large">
                Book Sourcing Demo
              </button>
            </div>
            <div className="talent-cta-features">
              <span>✓ 14-day free trial</span>
              <span>✓ No credit card required</span>
              <span>✓ Setup in 10 minutes</span>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CandidateSourcing;